import { Music, Music2, Heart, Clapperboard, Film } from "lucide-react";
import { Reveal, MaskedLine } from "./Reveal";
import { Watermark } from "./Logo";

const PORTRAIT =
  "https://customer-assets-v7afamib.emergentagent.net/job_danimorr/artifacts/9dabce3b8c011950_067eb84a-da49-4600-86fa-3fb615dfb455.jpg";
const PORTRAIT_SIDE =
  "https://customer-assets-v7afamib.emergentagent.net/job_danimorr/artifacts/e02176dfe36844e7_0ed4562a-0f6d-4f28-b494-1ec8977ce9ca.jpg";

const GLYPHS = [
  { Icon: Music, left: "4%", size: 20, delay: "0s", duration: "9s", color: "#C18C3D" },
  { Icon: Heart, left: "12%", size: 15, delay: "2.2s", duration: "11s", color: "#A3562A" },
  { Icon: Music2, left: "21%", size: 24, delay: "4.5s", duration: "10s", color: "#E6DFD3" },
  { Icon: Clapperboard, left: "30%", size: 18, delay: "1.4s", duration: "12s", color: "#C18C3D" },
  { Icon: Heart, left: "42%", size: 22, delay: "3.6s", duration: "9.5s", color: "#A3562A" },
  { Icon: Music, left: "52%", size: 16, delay: "5.8s", duration: "11.5s", color: "#C18C3D" },
  { Icon: Film, left: "61%", size: 20, delay: "0.8s", duration: "10.5s", color: "#E6DFD3" },
  { Icon: Heart, left: "70%", size: 14, delay: "4s", duration: "8.5s", color: "#C18C3D" },
  { Icon: Music2, left: "79%", size: 26, delay: "2.8s", duration: "12.5s", color: "#A3562A" },
  { Icon: Music, left: "88%", size: 18, delay: "6.4s", duration: "9s", color: "#E6DFD3" },
  { Icon: Heart, left: "94%", size: 19, delay: "1.9s", duration: "10.8s", color: "#A3562A" },
  { Icon: Clapperboard, left: "47%", size: 15, delay: "7.2s", duration: "11.8s", color: "#4A5844" },
];

const LoveInterlude = () => (
  <section
    data-testid="love-section"
    className="relative bg-ink border-t border-cream/10 py-24 lg:py-32 overflow-hidden"
  >
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
      {GLYPHS.map((g, i) => (
        <span
          key={i}
          className="float-glyph"
          style={{
            left: g.left,
            animationDelay: g.delay,
            animationDuration: g.duration,
            color: g.color,
          }}
        >
          <g.Icon size={g.size} strokeWidth={1.5} fill={g.Icon === Heart ? g.color : "none"} />
        </span>
      ))}
    </div>

    <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-5 flex justify-center lg:justify-start">
        <Reveal delay={0.1}>
          <div className="relative w-72 lg:w-[22rem] pt-8 pr-10">
            <div
              data-testid="heartbeat-glow"
              className="heartbeat-glow absolute -inset-4 rounded-full pointer-events-none"
              aria-hidden="true"
            />
            <div className="relative -rotate-2 hover:rotate-0 transition-transform duration-700">
              <div className="clip-frame border border-gold/50 overflow-hidden w-64 lg:w-80">
                <img
                  src={PORTRAIT}
                  alt="Dani Morr — color-blocked alley portrait, front stance"
                  data-testid="love-portrait"
                  className="w-full h-[340px] lg:h-[430px] object-cover"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="absolute top-0 right-0 rotate-6 hover:rotate-0 transition-transform duration-700 z-20">
              <div className="clip-frame-tl border border-amber/50 overflow-hidden w-32 lg:w-40 shadow-2xl">
                <img
                  src={PORTRAIT_SIDE}
                  alt="Dani Morr — color-blocked alley portrait, side profile"
                  data-testid="love-portrait-secondary"
                  className="w-full h-[170px] lg:h-[215px] object-cover"
                  loading="lazy"
                />
              </div>
            </div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cream/40 mt-4 text-center">
              Dani — Off Script
            </p>
          </div>
        </Reveal>
      </div>

      <div className="lg:col-span-7">
        <Reveal>
          <p className="font-mono text-xs text-gold tracking-widest mb-6">— INTERLUDE</p>
        </Reveal>
        <h2 className="font-serif font-light text-cream text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
          <MaskedLine delay={0.05}>For the love of</MaskedLine>
          <MaskedLine delay={0.15}>
            <em className="italic text-gold">music</em> &amp; <em className="italic text-amber">film.</em>
          </MaskedLine>
        </h2>
        <Reveal delay={0.25}>
          <p className="text-base lg:text-lg text-cream/60 leading-relaxed mt-7 max-w-xl">
            Every chord is a scene. Every frame has a rhythm. The two were never separate
            languages — just one voice, heard two ways.
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);

export default LoveInterlude;
